'use client';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode
} from 'react';
import { usePathname } from 'next/navigation';
import { menuApi } from '@/lib/api/menu';
import type { Menu } from '@/types/menu';
import type { NavItem } from '@/types';
import { Icons } from '@/components/icons';
import { getAuthToken } from '@/config/api';
import { getSiteTitle } from '@/lib/brand';

interface MenuContextType {
  menuItems: NavItem[];
  allMenus: Menu[]; // 完整的菜单数据（包括功能类型）
  loading: boolean;
  fetchMenu: () => Promise<void>;
  permissionKeys: Set<string>; // 所有功能权限唯一键集合（function_key），包含所有 is_menu_function === 2 的项
}

const MenuContext = createContext<MenuContextType>({
  menuItems: [],
  allMenus: [],
  loading: false,
  fetchMenu: async () => {},
  permissionKeys: new Set<string>()
});

const hiddenMenuUrls = new Set([
  '/dashboard/public-team',
  '/dashboard/mining-pool-release',
  '/dashboard/yy-price'
]);

export function MenuProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const [menuItems, setMenuItems] = useState<NavItem[]>([]);
  const [allMenus, setAllMenus] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(false);
  const [permissionKeys, setPermissionKeys] = useState<Set<string>>(new Set());

  // 根据路径递归查找菜单标题
  const findMenuTitleByPath = (menus: Menu[], path: string): string | null => {
    for (const menu of menus) {
      if (menu.page_url === path) {
        return menu.resource_name;
      }
      if (menu.items && menu.items.length > 0) {
        const found = findMenuTitleByPath(menu.items, path);
        if (found) {
          return found;
        }
      }
    }
    return null;
  };

  // 根据当前路径设置浏览器标签页标题
  useEffect(() => {
    if (!pathname || allMenus.length === 0) return;
    const title = findMenuTitleByPath(allMenus, pathname);
    if (title) {
      document.title = `${title} - ${getSiteTitle()}`;
    } else {
      document.title = getSiteTitle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, allMenus]);

  // 将后端菜单数据转换为 NavItem 格式
  const convertMenuToNavItem = (menu: Menu): NavItem | null => {
    // 只处理菜单类型（is_menu_function === 1），不处理功能类型（is_menu_function === 2）
    if (menu.is_menu_function !== 1) {
      return null;
    }
    if (menu.page_url && hiddenMenuUrls.has(menu.page_url)) {
      return null;
    }

    // 尝试获取图标，如果不存在则使用默认图标
    let icon: keyof typeof Icons | undefined = undefined;
    if (menu.icon) {
      // 尝试将图标名称映射到 Icons
      const iconKey = menu.icon as keyof typeof Icons;
      if (Icons[iconKey]) {
        icon = iconKey;
      } else {
        // 如果图标不存在，使用默认图标
        icon = 'settings';
      }
    } else {
      icon = 'settings';
    }

    const navItem: NavItem = {
      title: menu.resource_name,
      url: menu.page_url || menu.route || '#',
      icon: icon,
      isActive: false,
      items: []
    };

    // 如果有子菜单，递归转换并排序
    if (menu.items && menu.items.length > 0) {
      // 先按sort排序，然后转换
      const sortedItems = [...menu.items].sort(
        (a, b) => (a.sort || 99) - (b.sort || 99)
      );
      navItem.items = sortedItems
        .map((item) => convertMenuToNavItem(item))
        .filter((item): item is NavItem => item !== null);
    }

    return navItem;
  };

  // 获取菜单数据
  const fetchMenu = async () => {
    const token = getAuthToken();
    if (!token) {
      setMenuItems([]);
      setAllMenus([]);
      return;
    }

    setLoading(true);
    try {
      const response = await menuApi.getUserMenuFunction();
      console.log('Menu function response:', response);

      // apiRequest 已经提取了 data 字段，所以 response 可能是 { list: [...] } 或整个响应对象
      // 需要兼容两种格式
      let menuList: any[] = [];

      if (response && typeof response === 'object') {
        // 如果响应有 data.list（完整响应格式）
        if ('data' in response && response.data && 'list' in response.data) {
          menuList = Array.isArray(response.data.list)
            ? response.data.list
            : [];
        }
        // 如果响应直接有 list（已经提取了 data 的情况）
        else if ('list' in response && Array.isArray(response.list)) {
          menuList = response.list;
        }
      }

      if (menuList.length > 0) {
        // 先按sort排序，然后转换
        const sortedMenus = [...menuList].sort(
          (a, b) => (a.sort || 99) - (b.sort || 99)
        );

        // 保存完整的菜单数据（包括功能类型）
        setAllMenus(sortedMenus);

        // 转换菜单数据（只用于导航显示）
        const convertedMenus = sortedMenus
          .map((menu) => convertMenuToNavItem(menu))
          .filter((item): item is NavItem => item !== null);

        setMenuItems(convertedMenus);
        console.log('Converted menu items:', convertedMenus);
      } else {
        setMenuItems([]);
        setAllMenus([]);
      }
    } catch (error: any) {
      console.error('获取菜单失败:', error);
      setMenuItems([]);
      setAllMenus([]);
    } finally {
      setLoading(false);
    }
  };

  // 当有 token 时自动获取菜单
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      fetchMenu();
    } else {
      setMenuItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 收集所有菜单中 is_menu_function === 2 的功能权限唯一键集合（function_key）
  useEffect(() => {
    const collectAllFunctionKeys = (menus: Menu[]): Set<string> => {
      const keys = new Set<string>();

      // 递归遍历所有菜单项，收集所有 is_menu_function === 2 的 function_key
      const walk = (node: Menu) => {
        if (node.is_menu_function === 2 && node.function_key) {
          keys.add(node.function_key);
        }
        if (node.items && node.items.length > 0) {
          node.items.forEach(walk);
        }
      };

      // 遍历所有菜单
      menus.forEach(walk);

      return keys;
    };

    if (allMenus.length > 0) {
      const allKeys = collectAllFunctionKeys(allMenus);
      setPermissionKeys(allKeys);
    } else {
      setPermissionKeys(new Set());
    }
  }, [allMenus]);

  return (
    <MenuContext.Provider
      value={{ menuItems, allMenus, loading, fetchMenu, permissionKeys }}
    >
      {children}
    </MenuContext.Provider>
  );
}

export function useMenu() {
  return useContext(MenuContext);
}
